/**
 * Green Room screen (task 20.3 / task 19).
 *
 * The out-of-game actions: adjust an AI character, take over (→ human_pc), hand
 * back (→ ai_pc), withdraw (→ roster). Each is a button/form that calls the
 * Green Room endpoint; the server routes them through the authorized write path
 * and announces/audits them.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { CharacterLedger } from "@rufus/shared-types";
import { useServices } from "../AppContext.js";
import { friendlyErrorMessage, type GreenRoomAction } from "../api-client.js";

const ACTION_LABELS: Record<GreenRoomAction, string> = {
  adjust: "Adjust",
  take_over: "Take over",
  hand_back: "Hand back",
  withdraw: "Withdraw",
};

export function GreenRoomScreen() {
  const { api } = useServices();
  const { campaignId = "" } = useParams();
  const [roster, setRoster] = useState<readonly CharacterLedger[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .listCharacters()
      .then((r) => setRoster(r.filter((c) => c.currentCampaignId === campaignId)))
      .catch((e) => setError(friendlyErrorMessage(e, "Failed to load.")));
  };

  useEffect(load, [api, campaignId]);

  const act = async (characterId: string, action: GreenRoomAction) => {
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      await api.greenRoom({ campaignId, characterId, action });
      setMessage(`${ACTION_LABELS[action]} applied.`);
      load();
    } catch (err) {
      setError(friendlyErrorMessage(err, "Action failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Green Room">
      <p>
        <Link to="/campaigns">← Back to campaigns</Link>
        {" · "}
        <Link to={`/campaigns/${campaignId}/play`} data-testid="back-to-play">
          Back to play
        </Link>
      </p>
      <h1>Green Room</h1>
      <p>Out-of-game actions for characters in this campaign.</p>
      {message ? <p role="status">{message}</p> : null}
      {error ? <p role="alert">{error}</p> : null}

      {roster.length === 0 ? (
        <p>No characters in this campaign.</p>
      ) : (
        <ul data-testid="green-room-roster">
          {roster.map((c) => (
            <li key={c.characterId}>
              <span>
                <Link to={`/characters/${c.characterId}`}>{c.name}</Link> (
                {c.entityType})
              </span>
              {(Object.keys(ACTION_LABELS) as GreenRoomAction[]).map((action) => (
                <button
                  key={action}
                  type="button"
                  disabled={busy}
                  // Per-row accessible name so a screen-reader user can tell the
                  // otherwise-identical action buttons apart by character.
                  aria-label={`${ACTION_LABELS[action]} ${c.name}`}
                  onClick={() => void act(c.characterId, action)}
                >
                  {ACTION_LABELS[action]}
                </button>
              ))}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
